/**
 * Country redirect popup
 *
 * Detects the visitor's IP-based country via ipapi.co, pre-fills the Country
 * dropdown, and filters the Language dropdown accordingly. Clicking
 * "Shop now":
 *  - If the CURRENT store serves the chosen country (it's in one of this
 *    store's enabled markets) → POST /localization, switching the market
 *    in place exactly like the theme's native country drawer (e.g. Canada
 *    on the US store → Canada market / CAD, no redirect).
 *  - "Pull" countries always belong to their dedicated store (DE→de,
 *    GB+crown deps→uk, US/CA/MX/BR/AU/NZ + US-only Asia-Pacific→us, and
 *    countries only the DE store serves→de): selected from another store,
 *    they redirect to that store's domain with ?country=XX so the visitor
 *    lands in the right market context.
 *  - Any other country → redirect to its owning store (master list below)
 *    with ?country=XX (+ /locale/ subpath on the EU store).
 *
 * Hidden on /cart, /checkout, /account, /orders, /policies, /products,
 * /collections (configurable). Dismissal persisted in localStorage.
 */

const COUNTRY_REDIRECT_STORAGE_KEY = "country-redirect-dismissed";
const COUNTRY_REDIRECT_IP_CACHE_KEY = "country-redirect-ip-country";
const COUNTRY_REDIRECT_IP_CACHE_TTL_HOURS = 24 * 7; // 7 days

// Master country list shown on EVERY store. Generated 2026-08-28 by
// country-popup-2026-08-28/generate_master_list.py (sibling folder of the theme
// working copies, not tracked in the theme repos) from the live enabled markets
// of all four stores (union — see markets-snapshot.json there). Currency/symbol
// are what the owning store actually charges for that country. "store" = owning
// store; "pull: true" = always route to that store even when the current store
// also serves the country. Country names resolve at runtime via
// Intl.DisplayNames (English).
const COUNTRY_REDIRECT_MASTER_COUNTRIES = [
  { code: "AD", currency: "EUR", symbol: "€", store: "eu" },
  { code: "AE", currency: "USD", symbol: "$", store: "eu" },
  { code: "AF", currency: "AFN", symbol: "؋", store: "eu" },
  { code: "AL", currency: "EUR", symbol: "€", store: "eu" },
  { code: "AM", currency: "EUR", symbol: "€", store: "eu" },
  { code: "AT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "AU", currency: "AUD", symbol: "$", store: "us", pull: true },
  { code: "AX", currency: "EUR", symbol: "€", store: "eu" },
  { code: "BA", currency: "EUR", symbol: "€", store: "eu" },
  { code: "BE", currency: "EUR", symbol: "€", store: "eu" },
  { code: "BG", currency: "EUR", symbol: "€", store: "eu" },
  { code: "BH", currency: "USD", symbol: "$", store: "eu" },
  { code: "BR", currency: "BRL", symbol: "R$", store: "us", pull: true },
  { code: "CA", currency: "CAD", symbol: "$", store: "us", pull: true },
  { code: "CC", currency: "AUD", symbol: "$", store: "eu" },
  { code: "CH", currency: "CHF", symbol: "CHF", store: "eu" },
  { code: "CK", currency: "NZD", symbol: "$", store: "eu" },
  { code: "CX", currency: "AUD", symbol: "$", store: "eu" },
  { code: "CY", currency: "EUR", symbol: "€", store: "eu" },
  { code: "CZ", currency: "CZK", symbol: "Kč", store: "eu" },
  { code: "DE", currency: "EUR", symbol: "€", store: "de", pull: true },
  { code: "DK", currency: "DKK", symbol: "kr.", store: "eu" },
  { code: "EE", currency: "EUR", symbol: "€", store: "eu" },
  { code: "EG", currency: "EUR", symbol: "€", store: "eu" },
  { code: "ES", currency: "EUR", symbol: "€", store: "eu" },
  { code: "FI", currency: "EUR", symbol: "€", store: "eu" },
  { code: "FO", currency: "DKK", symbol: "kr.", store: "eu" },
  { code: "FR", currency: "EUR", symbol: "€", store: "eu" },
  { code: "GB", currency: "GBP", symbol: "£", store: "uk", pull: true },
  { code: "GE", currency: "EUR", symbol: "€", store: "eu" },
  { code: "GG", currency: "GBP", symbol: "£", store: "uk", pull: true },
  { code: "GI", currency: "GBP", symbol: "£", store: "uk", pull: true },
  { code: "GL", currency: "DKK", symbol: "kr.", store: "eu" },
  { code: "GP", currency: "EUR", symbol: "€", store: "eu" },
  { code: "GR", currency: "EUR", symbol: "€", store: "eu" },
  { code: "GS", currency: "GBP", symbol: "£", store: "eu" },
  { code: "HK", currency: "HKD", symbol: "$", store: "us", pull: true },
  { code: "HR", currency: "EUR", symbol: "€", store: "eu" },
  { code: "HU", currency: "EUR", symbol: "€", store: "eu" },
  { code: "IE", currency: "EUR", symbol: "€", store: "eu" },
  { code: "IL", currency: "USD", symbol: "$", store: "eu" },
  { code: "IM", currency: "GBP", symbol: "£", store: "uk", pull: true },
  { code: "IO", currency: "USD", symbol: "$", store: "eu" },
  { code: "IQ", currency: "EUR", symbol: "€", store: "eu" },
  { code: "IS", currency: "ISK", symbol: "kr", store: "eu" },
  { code: "IT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "JE", currency: "GBP", symbol: "£", store: "uk", pull: true },
  { code: "JO", currency: "USD", symbol: "$", store: "eu" },
  { code: "JP", currency: "JPY", symbol: "¥", store: "us", pull: true },
  { code: "KG", currency: "KGS", symbol: "som", store: "eu" },
  { code: "KH", currency: "KHR", symbol: "៛", store: "us", pull: true },
  { code: "KI", currency: "EUR", symbol: "€", store: "eu" },
  { code: "KW", currency: "USD", symbol: "$", store: "eu" },
  { code: "KZ", currency: "KZT", symbol: "₸", store: "eu" },
  { code: "LB", currency: "USD", symbol: "$", store: "eu" },
  { code: "LI", currency: "CHF", symbol: "CHF", store: "eu" },
  { code: "LT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "LU", currency: "EUR", symbol: "€", store: "eu" },
  { code: "LV", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MA", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MC", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MD", currency: "EUR", symbol: "€", store: "eu" },
  { code: "ME", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MK", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MQ", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "MX", currency: "MXN", symbol: "$", store: "us", pull: true },
  { code: "MY", currency: "MYR", symbol: "RM", store: "us", pull: true },
  { code: "NF", currency: "AUD", symbol: "$", store: "eu" },
  { code: "NL", currency: "EUR", symbol: "€", store: "eu" },
  { code: "NO", currency: "NOK", symbol: "kr", store: "eu" },
  { code: "NR", currency: "AUD", symbol: "$", store: "eu" },
  { code: "NU", currency: "NZD", symbol: "$", store: "eu" },
  { code: "NZ", currency: "NZD", symbol: "$", store: "us", pull: true },
  { code: "OM", currency: "USD", symbol: "$", store: "eu" },
  { code: "PL", currency: "PLN", symbol: "zł", store: "eu" },
  { code: "PM", currency: "EUR", symbol: "€", store: "eu" },
  { code: "PN", currency: "NZD", symbol: "$", store: "eu" },
  { code: "PS", currency: "ILS", symbol: "₪", store: "eu" },
  { code: "PT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "QA", currency: "USD", symbol: "$", store: "eu" },
  { code: "RE", currency: "EUR", symbol: "€", store: "eu" },
  { code: "RO", currency: "EUR", symbol: "€", store: "eu" },
  { code: "RU", currency: "EUR", symbol: "€", store: "de", pull: true },
  { code: "SA", currency: "USD", symbol: "$", store: "eu" },
  { code: "SB", currency: "SBD", symbol: "$", store: "eu" },
  { code: "SE", currency: "SEK", symbol: "kr", store: "eu" },
  { code: "SG", currency: "SGD", symbol: "$", store: "us", pull: true },
  { code: "SI", currency: "EUR", symbol: "€", store: "eu" },
  { code: "SJ", currency: "NOK", symbol: "kr", store: "eu" },
  { code: "SK", currency: "EUR", symbol: "€", store: "eu" },
  { code: "SM", currency: "EUR", symbol: "€", store: "eu" },
  { code: "TF", currency: "EUR", symbol: "€", store: "eu" },
  { code: "TK", currency: "NZD", symbol: "$", store: "eu" },
  { code: "TN", currency: "EUR", symbol: "€", store: "eu" },
  { code: "TO", currency: "TOP", symbol: "T$", store: "eu" },
  { code: "UA", currency: "UAH", symbol: "₴", store: "eu" },
  { code: "US", currency: "USD", symbol: "$", store: "us", pull: true },
  { code: "VA", currency: "EUR", symbol: "€", store: "eu" },
  { code: "XK", currency: "EUR", symbol: "€", store: "eu" },
  { code: "YT", currency: "EUR", symbol: "€", store: "eu" },
  { code: "ZA", currency: "EUR", symbol: "€", store: "eu" },
];

// Customer-facing host, myshopify domain (for store detection on previews /
// theme dev), and published storefront languages per store. The EU store is
// the fallback/"International" experience.
const COUNTRY_REDIRECT_STORES = {
  de: {
    host: "de.gobicashmere.com",
    shop: "gobicashmerede.myshopify.com",
    languages: [{ code: "de", name: "Deutsch" }],
  },
  eu: {
    host: "gobicashmere.com",
    shop: "cashmeregal.myshopify.com",
    languages: [
      { code: "en", name: "English" },
      { code: "cs", name: "Čeština" },
      { code: "da", name: "Dansk" },
      { code: "es", name: "Español" },
      { code: "fi", name: "Suomi" },
      { code: "fr", name: "Français" },
      { code: "it", name: "Italiano" },
      { code: "nl", name: "Nederlands" },
      { code: "pl", name: "Polski" },
      { code: "sv", name: "Svenska" },
    ],
  },
  uk: {
    host: "uk.gobicashmere.com",
    shop: "gobicashmereuk.myshopify.com",
    languages: [{ code: "en", name: "English" }],
  },
  us: {
    host: "us.gobicashmere.com",
    shop: "gobicashmereus.myshopify.com",
    languages: [{ code: "en", name: "English" }],
  },
};

const COUNTRY_REDIRECT_FALLBACK_STORE_KEY = "eu";

class CountryRedirectPopup extends ModalDialog {
  constructor() {
    super();
    this.currentCountry = (this.dataset.currentCountry || "").toUpperCase();
    this.currentCountryName = this.dataset.currentCountryName || "";
    this.currentLanguage = (this.dataset.currentLanguage || "").toLowerCase();
    this.delayMs = (parseInt(this.dataset.delay, 10) || 3) * 1000;
    this.cookieDays = parseInt(this.dataset.cookieDays, 10) || 30;
    this.excludePaths = (this.dataset.excludePaths || "")
      .split(",")
      .map(p => p.trim())
      .filter(Boolean);
    this.skipCountries = (this.dataset.skipCountries || "")
      .split(",")
      .map(c => c.trim().toUpperCase())
      .filter(Boolean);

    const config = this.loadConfig();
    this.countryToLanguage = config.countryToLanguage || {};
    this.availableCountries = config.availableCountries || [];
    this.availableLanguages = config.availableLanguages || [];

    this.currentStoreKey = this.detectCurrentStoreKey();

    this.headingTemplate = this.dataset.headingTemplate || "";

    this.fromLine = this.querySelector(".country-redirect-popup__from");
    this.countrySelect = this.querySelector(".js-country-select");
    this.languageSelect = this.querySelector(".js-language-select");
    this.shopNowButton = this.querySelector(".js-shop-now");
    this.detectedCountry = null;

    this.populateCountrySelect();
    this.attachListeners();
    this.bindCountryDrawerTriggers();
  }

  /**
   * Which of the four Gobi stores is this page being served from? Prefer the
   * myshopify domain (window.Shopify.shop — also set on theme dev / editor
   * previews where the hostname wouldn't match), fall back to the
   * customer-facing hostname.
   */
  detectCurrentStoreKey() {
    const shop = ((window.Shopify && window.Shopify.shop) || "").toLowerCase();
    const host = window.location.hostname.replace(/^www\./, "").toLowerCase();
    for (const [key, info] of Object.entries(COUNTRY_REDIRECT_STORES)) {
      if (shop && info.shop === shop) return key;
    }
    for (const [key, info] of Object.entries(COUNTRY_REDIRECT_STORES)) {
      if (info.host === host) return key;
    }
    return null;
  }

  masterEntry(code) {
    return COUNTRY_REDIRECT_MASTER_COUNTRIES.find(c => c.code === code) || null;
  }

  /** Is the country in one of the CURRENT store's enabled markets? */
  servedByCurrentStore(code) {
    return this.availableCountries.some(c => c.code === code);
  }

  /**
   * Should "Shop now" switch the market in place (POST /localization) rather
   * than redirect to another store? True when the current store serves the
   * country — unless:
   *  - the country is "pulled" to a dedicated store elsewhere (e.g. Germany
   *    selected on the EU store still goes to de.gobicashmere.com), or
   *  - another store owns the country and the current store does NOT publish
   *    the country's preferred language (e.g. France or Ireland selected on
   *    the German-only DE store → EU store, which has /fr/ and English).
   *    Austria/Switzerland on the DE store stay local: they prefer German.
   */
  isLocalSwitch(code) {
    const entry = this.masterEntry(code);
    if (entry && entry.pull && entry.store !== this.currentStoreKey) return false;
    if (!this.servedByCurrentStore(code)) return false;
    if (entry && entry.store !== this.currentStoreKey) {
      const preferred = this.countryToLanguage[code];
      return !!preferred && this.availableLanguages.some(l => l.code === preferred);
    }
    return true;
  }

  /**
   * Hijack clicks on the theme's country-drawer summary buttons (announcement
   * bar / header / footer / nav drawer). Instead of opening the native
   * <details> drawer, open this popup so the visitor gets the same UI
   * everywhere.
   */
  bindCountryDrawerTriggers() {
    // Capture phase so we run BEFORE the theme's MenuDrawer click handler
    // (which is attached directly to the summary element).
    // stopImmediatePropagation prevents that other handler from firing.
    document.addEventListener(
      "click",
      e => {
        const summary = e.target.closest('summary[id^="country-drawer-button-"]');
        if (!summary) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        // Make sure the <details> stays closed even if anything already
        // toggled it open before our listener fired.
        const details = summary.closest("details");
        if (details) {
          details.removeAttribute("open");
          details.classList.remove("menu-opening");
          const customEl = details.closest("country-drawer");
          if (customEl) {
            customEl.querySelectorAll(".menu-is-open").forEach(el => {
              el.classList.remove("menu-is-open");
              el.setAttribute("aria-expanded", "false");
            });
          }
        }
        this.openManually();
      },
      true
    );
  }

  /**
   * Manually open the popup (e.g. when triggered by clicking the country
   * drawer button). Pre-selects the visitor's current Shopify country and
   * skips the auto-show logic.
   */
  openManually() {
    // The trigger usually lives inside the mobile menu / header drawer. That
    // drawer (MenuDrawer) installs a permanent document "focusin" handler that
    // forces focus back inside itself while it's open — which steals focus
    // from this popup's <select> on touch devices, so the native picker never
    // opens. Close any open drawer first to release that handler.
    this.closeOpenMenuDrawers();

    const fallbackCode = this.currentCountry || "INTL";
    const preselectCode = this.masterEntry(fallbackCode) ? fallbackCode : "INTL";
    this.detectedCountry = preselectCode;
    // Visitor opened the popup themselves — they don't need the
    // "You're visiting from …" line.
    this.hideFromLine();
    this.preselect(preselectCode);
    this.show();
  }

  /**
   * Close any open menu/header/country/desktop drawer so MenuDrawer's
   * persistent focus-trap (a document "focusin" listener that yanks focus
   * back into the open drawer) stops intercepting taps on this popup's
   * form controls. Without this the Country/Language selects can't be
   * opened on mobile when the popup is launched from the menu.
   */
  closeOpenMenuDrawers() {
    let closedAny = false;
    document
      .querySelectorAll("menu-drawer, header-drawer, country-drawer, desktop-drawer")
      .forEach(drawer => {
        if (
          drawer.details &&
          drawer.details.classList.contains("menu-opening") &&
          typeof drawer.instantlyHideDrawer === "function"
        ) {
          drawer.instantlyHideDrawer();
          closedAny = true;
        }
      });
    if (closedAny) {
      document.body.classList.remove("drawer--is-open");
      document.body.style.width = "";
      document.body.style.top = "";
      const headerDiv = document.getElementById("header");
      if (headerDiv) headerDiv.classList.remove("menu-open");
    }
  }

  /**
   * Coordination with the newsletter popup: while the country-redirect popup
   * is still deciding whether to auto-show (and while it's open), the
   * newsletter popup should hold off so the two don't appear at once. The
   * newsletter listens for "country-redirect:resolved".
   */
  static markPending() {
    window.__countryRedirectPending = true;
  }

  static resolvePending() {
    if (!window.__countryRedirectPending) return;
    window.__countryRedirectPending = false;
    document.dispatchEvent(new CustomEvent("country-redirect:resolved"));
  }

  loadConfig() {
    const scriptEl = this.querySelector('script[type="application/json"][data-popup-config]');
    if (!scriptEl) return {};
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.warn("[country-redirect] Failed to parse config JSON:", e);
      return {};
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // ModalDialog re-parents the element to <body>, which triggers
    // connectedCallback a second time. Skip duplicate initialization so we
    // don't double-fire setTimeout / fetch.
    if (this.initialized) return;
    this.initialized = true;

    if (window.Shopify && window.Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        this.detectedCountry = this.currentCountry || "DE";
        this.renderFromLine(this.englishCountryName(this.detectedCountry) || this.detectedCountry);
        this.preselect(this.detectedCountry);
        this.show();
      }
      return;
    }

    if (this.isExcludedPath()) return;
    if (this.isDismissed()) return;

    // Hold the newsletter popup back until this one has resolved — shown and
    // closed, or decided not to show — so they never appear simultaneously.
    CountryRedirectPopup.markPending();

    setTimeout(async () => {
      await this.maybeShow();
      // maybeShow may decide not to show (wrong country, already on the right
      // store, etc.). If nothing opened, release the hold right away.
      if (!this.hasAttribute("open")) {
        CountryRedirectPopup.resolvePending();
      }
    }, this.delayMs);
  }

  isExcludedPath() {
    const path = window.location.pathname;
    return this.excludePaths.some(prefix => path === prefix || path.startsWith(prefix + "/"));
  }

  isDismissed() {
    try {
      const stored = localStorage.getItem(COUNTRY_REDIRECT_STORAGE_KEY);
      if (!stored) return false;
      const { expires } = JSON.parse(stored);
      if (expires && Date.now() > expires) {
        localStorage.removeItem(COUNTRY_REDIRECT_STORAGE_KEY);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  setDismissed() {
    const expires = Date.now() + this.cookieDays * 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(
        COUNTRY_REDIRECT_STORAGE_KEY,
        JSON.stringify({ expires })
      );
    } catch (e) {
      // ignore
    }
  }

  async maybeShow() {
    const ipCountry = await this.detectIpCountry();
    if (!ipCountry) return;

    // Skip popup entirely for countries listed in the section's
    // "Skip popup for these countries" setting.
    if (this.skipCountries.includes(ipCountry)) return;

    const entry = this.masterEntry(ipCountry);

    // Country isn't in any store's open markets → suggest the International
    // (EU root) experience, unless the visitor is already on the EU store.
    if (!entry) {
      if (this.currentStoreKey === COUNTRY_REDIRECT_FALLBACK_STORE_KEY) return;
      this.detectedCountry = "INTL";
      this.renderFromLine(this.englishCountryName(ipCountry) || "International");
      this.preselect("INTL");
      this.show();
      return;
    }

    if (this.isLocalSwitch(ipCountry)) {
      // Visitor is on a store that serves their country. Only suggest a
      // switch when the active market differs (e.g. Canadian browsing the US
      // store in the US market → offer the Canada market).
      if (ipCountry === this.currentCountry) return;
    }

    this.detectedCountry = ipCountry;
    this.renderFromLine(this.englishCountryName(ipCountry) || this.countryDisplayName(ipCountry));
    this.preselect(ipCountry);
    this.show();
  }

  async detectIpCountry() {
    // Cache the IP-detected country in localStorage so subsequent visits in
    // the next 7 days don't hit the API again.
    try {
      const cached = localStorage.getItem(COUNTRY_REDIRECT_IP_CACHE_KEY);
      if (cached) {
        const { country, expires } = JSON.parse(cached);
        if (expires > Date.now() && country) return country.toUpperCase();
        // expired → clear and continue
        if (expires && Date.now() > expires) {
          localStorage.removeItem(COUNTRY_REDIRECT_IP_CACHE_KEY);
        }
      }
    } catch (e) {
      // ignore
    }

    try {
      const response = await fetch("https://ipapi.co/json/", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const country = (data.country_code || data.country || "").toUpperCase();
      if (!country) return null;

      try {
        localStorage.setItem(
          COUNTRY_REDIRECT_IP_CACHE_KEY,
          JSON.stringify({
            country,
            expires: Date.now() + COUNTRY_REDIRECT_IP_CACHE_TTL_HOURS * 60 * 60 * 1000,
          })
        );
      } catch (e) {
        // ignore
      }

      return country;
    } catch (e) {
      console.warn("[country-redirect] IP detection failed:", e);
      return null;
    }
  }

  countryDisplayName(code) {
    const matched = this.availableCountries.find(c => c.code === code);
    if (matched) return matched.name;
    // Use Intl.DisplayNames to localize the country name in the visitor's
    // language (e.g. "Germany" in English, "Deutschland" in German).
    try {
      const localized = new Intl.DisplayNames(
        [this.currentLanguage || "en"],
        { type: "region" }
      ).of(code);
      if (localized) return localized;
    } catch (e) {
      // ignore
    }
    return code;
  }

  renderFromLine(countryName) {
    if (!this.fromLine || !this.headingTemplate) return;
    const filled = this.headingTemplate.replace(/\{country\}/g, countryName || "");
    this.fromLine.textContent = filled;
    this.fromLine.hidden = false;
  }

  hideFromLine() {
    if (this.fromLine) {
      this.fromLine.hidden = true;
      this.fromLine.textContent = "";
    }
  }

  populateCountrySelect() {
    if (!this.countrySelect) return;

    // Use the master list so all stores show the SAME comprehensive country
    // list regardless of which markets the current store has enabled. When
    // the selection would switch the market on the CURRENT store, show that
    // store's exact currency for the country (from localization data);
    // otherwise show the owning store's currency from the master list.
    const options = COUNTRY_REDIRECT_MASTER_COUNTRIES.map(c => {
      const local = this.isLocalSwitch(c.code)
        ? this.availableCountries.find(x => x.code === c.code)
        : null;
      const currency = (local && local.currency) || c.currency;
      const symbol = (local && local.symbol) || c.symbol;
      const englishName = this.englishCountryName(c.code) || c.code;
      return {
        value: c.code,
        label: `${englishName} (${currency}|${symbol})`,
        sortKey: englishName,
      };
    });

    options.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    // Always offer "International" as a fallback option at the top — used for
    // visitors from countries not in the master list.
    options.unshift({
      value: "INTL",
      label: "International (EUR|€)",
      sortKey: "",
    });

    this.countrySelect.innerHTML = options
      .map(o => `<option value="${o.value}">${this.escapeHtml(o.label)}</option>`)
      .join("");
  }

  englishCountryName(code) {
    try {
      return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || null;
    } catch (e) {
      return null;
    }
  }

  populateLanguageSelect(countryCode) {
    if (!this.languageSelect) return;

    let options;
    let preferred;

    if (countryCode === "INTL") {
      // International fallback — locked to English.
      options = [{ code: "en", name: "English" }];
      preferred = "en";
    } else if (this.isLocalSwitch(countryCode)) {
      // Market switch on the current store → offer this store's published
      // languages (from Liquid localization data).
      options = this.availableLanguages;
      preferred = this.countryToLanguage[countryCode];
      if (!options.some(o => o.code === preferred)) preferred = this.currentLanguage;
    } else {
      // Redirect to the owning store → offer that store's languages.
      const entry = this.masterEntry(countryCode);
      const storeKey = (entry && entry.store) || COUNTRY_REDIRECT_FALLBACK_STORE_KEY;
      options = COUNTRY_REDIRECT_STORES[storeKey].languages;
      preferred = this.countryToLanguage[countryCode];
    }

    if (!options || !options.length) options = [{ code: "en", name: "English" }];
    if (!options.some(o => o.code === preferred)) preferred = options[0].code;

    this.languageSelect.innerHTML = options
      .map(o => `<option value="${o.code}">${this.escapeHtml(o.name)}</option>`)
      .join("");
    this.languageSelect.value = preferred;
    this.languageSelect.disabled = options.length <= 1;
  }

  preselect(countryCode) {
    if (this.countrySelect && countryCode) {
      // Set the country value if option exists, otherwise fall back to current.
      const optionExists = Array.from(this.countrySelect.options).some(o => o.value === countryCode);
      this.countrySelect.value = optionExists ? countryCode : this.currentCountry;
    }
    this.populateLanguageSelect(this.countrySelect ? this.countrySelect.value : countryCode);
  }

  attachListeners() {
    if (this.countrySelect) {
      this.countrySelect.addEventListener("change", () => {
        this.populateLanguageSelect(this.countrySelect.value);
      });
    }
    if (this.shopNowButton) {
      this.shopNowButton.addEventListener("click", e => {
        e.preventDefault();
        this.handleShopNow();
      });
    }
  }

  escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  handleShopNow() {
    const country = this.countrySelect ? this.countrySelect.value : "";
    const language = this.languageSelect ? this.languageSelect.value : this.currentLanguage;

    this.setDismissed();

    // International option → fallback (EU) root in English.
    if (country === "INTL") {
      window.location.href = `https://${COUNTRY_REDIRECT_STORES[COUNTRY_REDIRECT_FALLBACK_STORE_KEY].host}/`;
      return;
    }

    // The current store serves this country → switch the market in place via
    // Shopify's localization endpoint, exactly like the native country
    // drawer (keeps the visitor, their cart and their page).
    if (this.isLocalSwitch(country)) {
      if (country === this.currentCountry && (!language || language === this.currentLanguage)) {
        this.hide();
        return;
      }
      this.submitLocalizationChange(country, language);
      return;
    }

    // Another store owns this country → redirect there. ?country=XX makes
    // Shopify activate the right market on arrival (verified: it sets the
    // localization + cart_currency cookies), and EU locales get their
    // subfolder (e.g. /fr/).
    const entry = this.masterEntry(country);
    const storeKey = (entry && entry.store) || COUNTRY_REDIRECT_FALLBACK_STORE_KEY;
    const store = COUNTRY_REDIRECT_STORES[storeKey];
    const langCodes = store.languages.map(l => l.code);
    const lang = langCodes.includes(language) ? language : langCodes[0];
    const subpath = storeKey === COUNTRY_REDIRECT_FALLBACK_STORE_KEY && lang && lang !== "en" ? `${lang}/` : "";
    window.location.href = `https://${store.host}/${subpath}?country=${encodeURIComponent(country)}`;
  }

  submitLocalizationChange(countryCode, languageCode) {
    const form = document.createElement("form");
    form.method = "POST";
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    form.action = root + "localization";
    form.style.display = "none";

    const fields = {
      _method: "PUT",
      form_type: "localization",
      utf8: "✓",
      country_code: countryCode,
      language_code: languageCode || this.currentLanguage,
      return_to: window.location.pathname + window.location.search,
    };

    Object.entries(fields).forEach(([name, value]) => {
      if (!value) return;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  show(opener) {
    const previousFocus = opener || document.activeElement;
    super.show(previousFocus);
    if (this.classList.contains("country-redirect-popup--blurred")) {
      document.body.classList.add("country-redirect-popup-is-open");
    }
  }

  hide() {
    // Any way of closing (X, Esc, backdrop, or Shop now) counts as dismissal.
    this.setDismissed();
    super.hide();
    document.body.classList.remove("country-redirect-popup-is-open");
    // Popup dismissed — let the newsletter popup proceed.
    CountryRedirectPopup.resolvePending();
    if (this.getAttribute("data-open-in-design-mode")) {
      this.removeAttribute("data-open-in-design-mode");
    }
  }
}

customElements.define("country-redirect-popup", CountryRedirectPopup);
